<template>
  <div class="w-full max-w-lg mx-auto rounded-2xl bg-white shadow-lg border border-slate-200/80 overflow-hidden">
    <!-- Header -->
    <div class="bg-subDarkBlue px-5 py-4">
      <h2 class="text-lg font-bold text-white text-center tracking-tight">
        {{ $t("Pay once for this order") }}
      </h2>
    </div>

    <div class="p-5 space-y-5">
      <!-- Duration range (user selects for upload and video/audio link; YouTube derives from API) -->
      <div v-if="sourceType !== 'youtube'">
        <p class="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">{{ $t("Duration range") }}</p>
        <p class="text-xs text-slate-600 mb-2">{{ $t("duration-range-explanation") }}</p>
        <div class="grid grid-cols-1 gap-2">
          <label
            v-for="opt in durationOptions"
            :key="opt.value"
            class="flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all"
            :class="durationRange === opt.value
              ? 'border-subDarkBlue bg-subWhite/50'
              : 'border-slate-200 hover:border-slate-300 bg-slate-50/50'"
          >
            <input v-model="durationRange" type="radio" :value="opt.value" class="sr-only" />
            <span
              class="flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors"
              :class="durationRange === opt.value ? 'border-subDarkBlue bg-subDarkBlue' : 'border-slate-300'"
            >
              <span v-if="durationRange === opt.value" class="h-1.5 w-1.5 rounded-full bg-white" />
            </span>
            <span class="text-sm font-medium text-slate-800">{{ $t(opt.label) }}</span>
          </label>
        </div>
      </div>

      <!-- Services -->
      <div>
        <p class="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">{{ $t("Services") }}</p>
        <div class="space-y-2">
          <label class="flex items-center gap-3 p-3 rounded-xl bg-slate-100 border border-slate-200 cursor-not-allowed opacity-80">
            <input type="checkbox" checked disabled class="rounded border-slate-300 text-subDarkBlue" />
            <span class="text-sm font-medium text-slate-600">{{ $t("transcription") }}</span>
          </label>
          <label class="flex items-center gap-3 p-3 rounded-xl border border-slate-200 cursor-pointer hover:bg-slate-50 transition-colors">
            <input v-model="services.translation" type="checkbox" class="rounded border-slate-300 text-subDarkBlue focus:ring-subDarkBlue" />
            <span class="text-sm font-medium text-slate-800">{{ $t("translation") }}</span>
          </label>
          <label class="flex items-center gap-3 p-3 rounded-xl border border-slate-200 cursor-pointer hover:bg-slate-50 transition-colors">
            <input v-model="services.captionTranslation" type="checkbox" class="rounded border-slate-300 text-subDarkBlue focus:ring-subDarkBlue" />
            <span class="text-sm font-medium text-slate-800">{{ $t("Caption translation") }}</span>
          </label>
          <label class="flex items-center gap-3 p-3 rounded-xl border border-slate-200 cursor-pointer hover:bg-slate-50 transition-colors">
            <input v-model="services.voiceOver" type="checkbox" class="rounded border-slate-300 text-subDarkBlue focus:ring-subDarkBlue" />
            <span class="text-sm font-medium text-slate-800">{{ $t("Voice over") }}</span>
          </label>
        </div>
        <p
          v-if="activeCharLimit != null"
          class="text-xs text-amber-900/90 bg-amber-50 border border-amber-200/80 rounded-lg px-3 py-2 mt-3"
        >
          {{ $t("one-time-translation-tts-char-limit", { count: activeCharLimit.toLocaleString() }) }}
        </p>
      </div>

      <!-- Target language -->
      <div v-if="services.translation || services.voiceOver || services.captionTranslation">
        <p class="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">{{ $t("Target language") }}</p>
        <select
          v-model="targetLang"
          class="w-full rounded-xl border-2 border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-800 focus:border-subDarkBlue focus:ring-2 focus:ring-subDarkBlue/20 outline-none transition-colors"
        >
          <option value="">— {{ $t("Select") }} —</option>
          <option
            v-for="lang in myLanguagesList"
            :key="lang.langCode"
            :value="lang.langCode"
          >
            {{ lang.langName }}
          </option>
        </select>
      </div>

      <!-- Pricing error -->
      <div v-if="priceError" class="flex items-center gap-2 rounded-xl bg-red-50 border border-red-200 px-4 py-3">
        <svg class="h-5 w-5 shrink-0 text-red-500" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
        </svg>
        <p class="text-sm font-medium text-red-700">{{ priceError }}</p>
      </div>

      <!-- Total -->
      <div v-if="sourceType === 'youtube' && (youtubePricingLoading || !youtubePricing) && !priceError" class="rounded-xl bg-subWhite/60 border border-subLightBlue/30 px-4 py-4 text-center">
        <p class="text-sm text-slate-600">{{ $t("Loading price...") }}</p>
      </div>
      <div v-else-if="sourceType === 'youtube' && youtubePricing && !priceError" class="rounded-xl bg-subWhite/60 border border-subLightBlue/30 px-4 py-4 text-center">
        <p class="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-0.5">{{ $t("Duration range") }}</p>
        <p class="text-sm font-medium text-slate-700 mb-1">{{ $t(youtubeDurationRangeLabel) }}</p>
        <p class="text-xs text-slate-600 mb-2">{{ $t("duration-range-explanation") }}</p>
        <p class="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-0.5">{{ $t("total") }}</p>
        <p class="text-2xl font-bold text-subDarkBlue">{{ currency }} {{ (youtubeTotalPrice ?? 0).toFixed(2) }}</p>
      </div>
      <div v-else-if="sourceType !== 'youtube' && totalPrice !== null && !priceError" class="rounded-xl bg-subWhite/60 border border-subLightBlue/30 px-4 py-4 text-center">
        <p class="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-0.5">{{ $t("Duration range") }}</p>
        <p class="text-sm font-medium text-slate-700 mb-2">{{ durationRangeLabel }}</p>
        <p class="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-0.5">{{ $t("total") }}</p>
        <p class="text-2xl font-bold text-subDarkBlue">{{ currency }} {{ (totalPrice ?? 0).toFixed(2) }}</p>
      </div>

      <!-- Actions -->
      <div class="flex gap-3 pt-1">
        <button
          type="button"
          @click="$emit('cancel')"
          class="flex-1 py-3 px-4 rounded-xl border-2 border-slate-300 text-slate-700 font-semibold text-sm hover:bg-slate-50 hover:border-slate-400 transition-colors"
        >
          {{ $t("cancel") }}
        </button>
        <button
          type="button"
          :disabled="!canPay || isPaying"
          @click="payWithPayPal"
          class="flex-1 py-3 px-4 rounded-xl bg-subDark text-white font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-95 transition-opacity flex items-center justify-center gap-2"
        >
          <svg v-if="isPaying" class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          {{ isPaying ? $t("Redirecting...") : $t("Pay with PayPal") }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from "vue";
import { useAudioStore } from "../../../store/audioStore";
import { useAuthStore } from "../../../store/auth";
import { API_BASE_URL, ONETIME_AUDIO_PRICING, ONETIME_AUDIO_YOUTUBE_PRICING, ONETIME_AUDIO_CREATE_ORDER } from "../../../utils/appConst";

const emit = defineEmits<{ (e: "cancel"): void }>();

const props = withDefaults(
  defineProps<{
    sourceType?: "audio" | "video" | "youtube";
    youtubeLink?: string;
    videoLink?: string;
    audioLink?: string;
    sourceLang?: string;
  }>(),
  { sourceType: "audio", youtubeLink: "", videoLink: "", audioLink: "", sourceLang: "" }
);

const durationOptions = [
  { value: "RANGE_0_10", label: "Up to 10 min" },
  { value: "RANGE_10_60", label: "Up to 1 hour" },
  { value: "RANGE_60_240", label: "1 to 4 hours" },
];

const authStore = useAuthStore();
const as = useAudioStore();

const durationRange = ref("RANGE_0_10");
const services = ref({ transcription: true, translation: false, captionTranslation: false, voiceOver: false });
const targetLang = ref("");
const pricing = ref<Array<{
  durationRange: string;
  transcriptionPrice: number;
  translationPrice: number;
  voiceOverPrice: number;
  captionTranslationPrice: number;
  maxCharsTranslationAndTts?: number;
  currency: string;
}> | null>(null);
const priceError = ref("");
const isPaying = ref(false);
const youtubePricing = ref<{
  durationSeconds: number;
  durationRange: string;
  transcriptionPrice: number;
  translationPrice: number;
  voiceOverPrice: number;
  captionTranslationPrice: number;
  maxCharsTranslationAndTts?: number;
  currency: string;
} | null>(null);
const youtubePricingLoading = ref(false);

const myLanguagesList = computed(() => as.myLanguagesList.filter((l) => l.langCode));

const currency = computed(() => {
  if (props.sourceType === "youtube" && youtubePricing.value) return youtubePricing.value.currency;
  return pricing.value?.[0]?.currency ?? "USD";
});

const durationRangeLabel = computed(() => {
  const opt = durationOptions.find((o) => o.value === durationRange.value);
  return opt ? opt.label : durationRange.value;
});

const totalPrice = computed(() => {
  if (!pricing.value || !durationRange.value) return null;
  const row = pricing.value.find((p) => p.durationRange === durationRange.value);
  if (!row) return null;
  let total = row.transcriptionPrice;
  if (services.value.translation) total += row.translationPrice;
  if (services.value.captionTranslation) total += row.captionTranslationPrice;
  if (services.value.voiceOver) total += row.voiceOverPrice;
  return total;
});

const youtubeTotalPrice = computed(() => {
  if (!youtubePricing.value) return null;
  let total = youtubePricing.value.transcriptionPrice;
  if (services.value.translation) total += youtubePricing.value.translationPrice;
  if (services.value.captionTranslation) total += youtubePricing.value.captionTranslationPrice;
  if (services.value.voiceOver) total += youtubePricing.value.voiceOverPrice;
  return total;
});

const youtubeDurationRangeLabel = computed(() => {
  if (!youtubePricing.value) return "";
  const opt = durationOptions.find((o) => o.value === youtubePricing.value!.durationRange);
  return opt ? opt.label : youtubePricing.value.durationRange;
});

const activeCharLimit = computed((): number | null => {
  if (!services.value.translation && !services.value.captionTranslation && !services.value.voiceOver) {
    return null;
  }
  if (props.sourceType === "youtube" && youtubePricing.value) {
    const y = youtubePricing.value.maxCharsTranslationAndTts;
    if (y != null) return y;
    const row = pricing.value?.find((p) => p.durationRange === youtubePricing.value!.durationRange);
    if (row?.maxCharsTranslationAndTts != null) return row.maxCharsTranslationAndTts;
    return null;
  }
  const row = pricing.value?.find((p) => p.durationRange === durationRange.value);
  if (row?.maxCharsTranslationAndTts != null) return row.maxCharsTranslationAndTts;
  return null;
});

const canPay = computed(() => {
  if (props.sourceType === "youtube") {
    if (!props.youtubeLink?.trim()) return false;
    if (youtubePricingLoading.value || !youtubePricing.value) return false;
    if (services.value.translation || services.value.voiceOver || services.value.captionTranslation) {
      if (!targetLang.value?.trim()) return false;
    }
    return youtubeTotalPrice.value !== null && youtubeTotalPrice.value > 0;
  }
  if (props.videoLink?.trim()) {
    if (!pricing.value || !durationRange.value) return false;
    if (services.value.translation || services.value.voiceOver || services.value.captionTranslation) {
      if (!targetLang.value?.trim()) return false;
    }
    return totalPrice.value !== null && totalPrice.value > 0;
  }
  if (props.audioLink?.trim()) {
    if (!pricing.value || !durationRange.value) return false;
    if (services.value.translation || services.value.voiceOver || services.value.captionTranslation) {
      if (!targetLang.value?.trim()) return false;
    }
    return totalPrice.value !== null && totalPrice.value > 0;
  }
  if (services.value.translation || services.value.voiceOver || services.value.captionTranslation) {
    if (!targetLang.value?.trim()) return false;
  }
  return totalPrice.value !== null && totalPrice.value > 0;
});

async function fetchPricing() {
  priceError.value = "";
  if (!authStore.token) {
    priceError.value = "Could not load pricing.";
    pricing.value = null;
    return;
  }
  try {
    const res = await fetch(`${API_BASE_URL}${ONETIME_AUDIO_PRICING}`, {
      headers: {
        Authorization: `Bearer ${authStore.token}`,
      },
    });
    if (!res.ok) throw new Error("Failed to load pricing");
    const data = await res.json();
    pricing.value = data.pricing || [];
  } catch (e) {
    priceError.value = "Could not load pricing.";
    pricing.value = null;
  }
}

async function fetchYoutubePricing() {
  if (props.sourceType !== "youtube" || !props.youtubeLink?.trim() || !authStore.token) {
    youtubePricing.value = null;
    return;
  }
  priceError.value = "";
  youtubePricingLoading.value = true;
  youtubePricing.value = null;
  try {
    const url = `${API_BASE_URL}${ONETIME_AUDIO_YOUTUBE_PRICING}?youtubeLink=${encodeURIComponent(props.youtubeLink.trim())}`;
    const res = await fetch(url, {
      headers: { Authorization: `Bearer ${authStore.token}` },
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "Failed to load price for this video");
    youtubePricing.value = {
      durationSeconds: data.durationSeconds,
      durationRange: data.durationRange,
      transcriptionPrice: data.transcriptionPrice,
      translationPrice: data.translationPrice,
      voiceOverPrice: data.voiceOverPrice,
      captionTranslationPrice: data.captionTranslationPrice ?? 0,
      maxCharsTranslationAndTts: data.maxCharsTranslationAndTts,
      currency: data.currency || "USD",
    };
  } catch (e) {
    priceError.value = (e as Error).message || "Could not get video price.";
    youtubePricing.value = null;
  } finally {
    youtubePricingLoading.value = false;
  }
}

async function payWithPayPal() {
  if (!canPay.value || !authStore.token) return;
  isPaying.value = true;
  const payload: Record<string, unknown> = {
    services: ["transcription", ...(services.value.translation ? ["translation"] : []), ...(services.value.captionTranslation ? ["captionTranslation"] : []), ...(services.value.voiceOver ? ["voiceOver"] : [])],
    targetLang: (services.value.translation || services.value.voiceOver || services.value.captionTranslation) ? targetLang.value?.trim() || null : null,
    sourceType: props.sourceType,
  };
  if (props.sourceType === "youtube") {
    payload.youtubeLink = props.youtubeLink?.trim() || "";
    payload.sourceLang = props.sourceLang?.trim() || null;
  } else if (props.videoLink?.trim()) {
    payload.videoLink = props.videoLink.trim();
    payload.sourceLang = props.sourceLang?.trim() || null;
    payload.durationRange = durationRange.value;
  } else if (props.audioLink?.trim()) {
    payload.audioLink = props.audioLink.trim();
    payload.sourceLang = props.sourceLang?.trim() || null;
    payload.durationRange = durationRange.value;
  } else {
    payload.durationRange = durationRange.value;
  }
  console.log("[OneTimePay] createOrder payload", payload);
  try {
    const res = await fetch(`${API_BASE_URL}${ONETIME_AUDIO_CREATE_ORDER}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authStore.token}`,
      },
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    console.log("[OneTimePay] createOrder response ok=", res.ok, "orderId=", data.orderId, "url=", !!data.url, "audioLinkFlow=", !!props.audioLink?.trim());
    if (!res.ok) throw new Error(data.error || "Create order failed");
    if (data.url) {
      if (props.sourceType === "youtube" && props.youtubeLink) {
        try {
          sessionStorage.setItem("onetime_youtube_link", props.youtubeLink);
          if (props.sourceLang) sessionStorage.setItem("onetime_youtube_sourceLang", props.sourceLang);
        } catch (_) {}
      }
      if (props.videoLink?.trim()) {
        try {
          sessionStorage.setItem("onetime_video_link", props.videoLink.trim());
          if (props.sourceLang) sessionStorage.setItem("onetime_video_sourceLang", props.sourceLang);
        } catch (_) {}
      }
      if (props.audioLink?.trim()) {
        try {
          sessionStorage.setItem("onetime_audio_link", props.audioLink.trim());
          if (props.sourceLang) sessionStorage.setItem("onetime_audio_sourceLang", props.sourceLang);
          console.log("[OneTimeAudioLink] payWithPayPal redirecting to PayPal, sessionStorage set for return orderId=", data.orderId);
        } catch (_) {}
      }
      window.location.href = data.url;
    } else {
      isPaying.value = false;
    }
  } catch (e) {
    isPaying.value = false;
    priceError.value = (e as Error).message || "Payment failed.";
  }
}

onMounted(fetchPricing);
watch(durationRange, fetchPricing);
watch(
  () => [props.sourceType, props.youtubeLink] as const,
  () => {
    if (props.sourceType === "youtube") fetchYoutubePricing();
    else youtubePricing.value = null;
  },
  { immediate: true }
);
</script>
